import React, { useState, useCallback } from 'react';
import { ChevronRight, Plus } from 'lucide-react';
import { useEditorStore, useCreativeStore, useTemplateStore } from '@/stores';
import { PROVIDERS, createCustomFormat } from '@/components/providers';
import type { AdFormat, Provider } from '@/types/formats';
import { BrushPicker } from '@/components/ui/BrushPicker';
import { solid } from '@/types/brush';
import type { BackgroundBrush } from '@/types/brush';

export function FormatPanel() {
  const { selectedFormat, setFormat } = useEditorStore();
  const { currentCreativeId, updateCreative } = useCreativeStore();
  const { currentTemplate, updateCanvasBackground } = useTemplateStore();
  const [expandedProvider, setExpandedProvider] = useState<Provider | null>('facebook');
  const [showCustom, setShowCustom] = useState(false);
  const [customWidth, setCustomWidth] = useState('1080');
  const [customHeight, setCustomHeight] = useState('1080');
  const [customName, setCustomName] = useState('Custom');

  // Update both editor state and creative's primaryFormatId
  const handleFormatChange = useCallback((format: AdFormat) => {
    setFormat(format);
    if (currentCreativeId) {
      updateCreative(currentCreativeId, { primaryFormatId: format.id });
    }
  }, [setFormat, currentCreativeId, updateCreative]);

  const handleCreateCustom = () => {
    const w = parseInt(customWidth);
    const h = parseInt(customHeight);
    if (w > 0 && h > 0) {
      const format = createCustomFormat(customName || 'Custom', w, h);
      handleFormatChange(format);
      setShowCustom(false);
    }
  };

  return (
    <div className="grid grid-cols-[280px_1fr] gap-6">
      {/* Provider tree */}
      <div className="space-y-2">
        <h3 className="label">Ad Formats</h3>
        {PROVIDERS.map((provider) => (
          <div key={provider.id}>
            <button
              onClick={() =>
                setExpandedProvider(
                  expandedProvider === provider.id ? null : provider.id
                )
              }
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors"
            >
              <ChevronRight
                size={14}
                className={`transform transition-transform ${
                  expandedProvider === provider.id ? 'rotate-90' : ''
                }`}
              />
              <provider.icon size={14} />
              <span className="text-sm font-medium">{provider.name}</span>
              <span className="text-xs text-fg-subtle ml-auto">
                {provider.formats.length}
              </span>
            </button>

            {expandedProvider === provider.id && (
              <div className="ml-6 mt-1 space-y-1">
                {provider.formats.map((format) => (
                  <FormatButton
                    key={format.id}
                    format={format}
                    isSelected={selectedFormat.id === format.id}
                    onClick={() => handleFormatChange(format)}
                  />
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Custom format */}
        <button
          onClick={() => setShowCustom(!showCustom)}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors mt-4"
        >
          <Plus size={14} />
          <span className="text-sm font-medium">Custom Size</span>
        </button>
      </div>

      {/* Right side: Selected format details or custom creator */}
      <div>
        {showCustom ? (
          <div className="space-y-4">
            <h3 className="label">Create Custom Format</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Name</label>
                <input
                  type="text"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  className="input"
                  placeholder="Custom"
                />
              </div>
              <div />
              <div>
                <label className="label">Width (px)</label>
                <input
                  type="number"
                  value={customWidth}
                  onChange={(e) => setCustomWidth(e.target.value)}
                  className="input"
                  min="1"
                />
              </div>
              <div>
                <label className="label">Height (px)</label>
                <input
                  type="number"
                  value={customHeight}
                  onChange={(e) => setCustomHeight(e.target.value)}
                  className="input"
                  min="1"
                />
              </div>
            </div>
            <button onClick={handleCreateCustom} className="btn-primary">
              Create Format
            </button>
          </div>
        ) : (
          <div>
            <h3 className="label">Selected Format</h3>
            <div className="bg-white/5 rounded-xl p-4">
              <div className="flex items-start gap-4">
                {/* Preview thumbnail */}
                <div
                  className="bg-navy rounded flex-none flex items-center justify-center"
                  style={{
                    width: Math.min(120, selectedFormat.width / 10),
                    height: Math.min(80, selectedFormat.height / 10),
                  }}
                >
                  <span className="text-xs text-fg-subtle">
                    {selectedFormat.width}×{selectedFormat.height}
                  </span>
                </div>
                <div>
                  <h4 className="font-semibold text-fg">{selectedFormat.name}</h4>
                  <p className="text-sm text-fg-muted mt-1">
                    {selectedFormat.width} × {selectedFormat.height} pixels
                  </p>
                  <p className="text-xs text-fg-subtle mt-2">
                    {selectedFormat.platforms}
                  </p>
                </div>
              </div>
            </div>

            {/* Quick format switcher */}
            <div className="mt-4">
              <h3 className="label">Quick Switch</h3>
              <div className="flex flex-wrap gap-2">
                {PROVIDERS.flatMap((p) => p.formats.slice(0, 2)).map((format) => (
                  <button
                    key={format.id}
                    onClick={() => handleFormatChange(format)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      selectedFormat.id === format.id
                        ? 'bg-primary text-white'
                        : 'bg-white/5 text-fg-muted hover:text-fg hover:bg-white/10'
                    }`}
                  >
                    {format.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Canvas Background */}
            <div className="mt-6">
              <h3 className="label">Canvas Background</h3>
              <div className="bg-white/5 rounded-xl p-4">
                <BrushPicker
                  value={currentTemplate?.canvas.background ?? solid('#0A1628')}
                  onChange={(brush) => updateCanvasBackground(brush as BackgroundBrush)}
                  showTransparent={true}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FormatButton({
  format,
  isSelected,
  onClick,
}: {
  format: AdFormat;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
        isSelected
          ? 'bg-primary/20 text-fg'
          : 'text-fg-muted hover:bg-white/5 hover:text-fg'
      }`}
    >
      <span className="font-medium">{format.name}</span>
      <span className="text-xs text-fg-subtle ml-2">
        {format.width}×{format.height}
      </span>
    </button>
  );
}
